import React, { useEffect, useState } from 'react';
import { ArrowPathIcon, LinkIcon, XCircleIcon } from '@heroicons/react/24/outline';
import {
  useDisconnectGoogleEmailV1SettingsGoogleEmailDisconnectPost,
  useGetGoogleEmailConfigV1SettingsGoogleEmailConfigGet,
  useUpdateGoogleEmailConfigV1SettingsGoogleEmailConfigPut,
} from '../../generated/settings/settings';
import { useToasts } from '../../stores/useToasts';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Page,
  PageBody,
  PageHeader,
  Spinner,
} from '../../components/ui';

function getErrorMessage(error: unknown, fallback: string): string {
  const candidate = error as { response?: { data?: { detail?: unknown } } };
  const detail = candidate?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((entry) => {
        if (entry && typeof entry === 'object' && 'msg' in entry) {
          const msg = (entry as { msg?: unknown }).msg;
          return typeof msg === 'string' ? msg : JSON.stringify(entry);
        }
        return JSON.stringify(entry);
      })
      .join(' | ');
  }
  return fallback;
}

export default function GoogleEmailIntegrationPage() {
  const { push } = useToasts();
  const configQuery = useGetGoogleEmailConfigV1SettingsGoogleEmailConfigGet();
  const updateConfigMutation = useUpdateGoogleEmailConfigV1SettingsGoogleEmailConfigPut();
  const disconnectMutation = useDisconnectGoogleEmailV1SettingsGoogleEmailDisconnectPost();

  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [redirectUri, setRedirectUri] = useState('');

  useEffect(() => {
    const config = configQuery.data;
    if (!config) return;
    setClientId(config.client_id || '');
    setClientSecret('');
    setRedirectUri(config.redirect_uri || '');
  }, [configQuery.data]);

  const handleSave = async () => {
    try {
      await updateConfigMutation.mutateAsync({
        data: {
          client_id: clientId.trim() || null,
          client_secret: clientSecret.trim() || null,
          redirect_uri: redirectUri.trim() || null,
        },
      });
      await configQuery.refetch();
      setClientSecret('');
      push({ kind: 'success', message: 'Google email settings saved' });
    } catch (error) {
      push({ kind: 'error', message: getErrorMessage(error, 'Failed to save Google email settings') });
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectMutation.mutateAsync();
      await configQuery.refetch();
      push({ kind: 'success', message: 'Google email account disconnected' });
    } catch (error) {
      push({ kind: 'error', message: getErrorMessage(error, 'Failed to disconnect Google email account') });
    }
  };

  const isLoading = configQuery.isLoading;
  const isSaving = updateConfigMutation.isPending;
  const isDisconnecting = disconnectMutation.isPending;
  const config = configQuery.data;

  if (isLoading) {
    return (
      <Page maxWidth="xl">
        <PageBody>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Spinner size="sm" />
            Loading Google email integration settings...
          </div>
        </PageBody>
      </Page>
    );
  }

  return (
    <Page maxWidth="xl">
      <PageHeader
        title="Google Email Integration"
        description="Configure Google OAuth credentials for AI Recruiter candidate outreach."
        actions={
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" className="mr-2" /> : null}
            Save Settings
          </Button>
        }
      />
      <PageBody className="space-y-6 pb-10">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LinkIcon className="h-5 w-5 text-eliza-red" />
              Connection Status
            </CardTitle>
            <CardDescription>
              This integration is product-specific and used for recruiter outreach via Gmail.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={config?.is_connected ? 'success' : 'warning'}>
                {config?.is_connected ? 'Connected' : 'Not Connected'}
              </Badge>
              {config?.connected_email ? <Badge variant="info">{config.connected_email}</Badge> : null}
            </div>
            {!config?.is_connected ? (
              <Alert variant="warning">
                A Gmail account is not currently connected. Save valid OAuth settings, then connect in workflow.
              </Alert>
            ) : null}
            <div>
              <Button
                variant="ghost"
                onClick={handleDisconnect}
                disabled={!config?.is_connected || isDisconnecting}
                className="text-red-600 hover:text-red-700"
              >
                {isDisconnecting ? <Spinner size="sm" className="mr-2" /> : <XCircleIcon className="mr-2 h-4 w-4" />}
                Disconnect Google Account
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>OAuth Credentials</CardTitle>
            <CardDescription>Store Google OAuth client settings used by recruiter email workflows.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="google_client_id">Client ID</Label>
              <Input
                id="google_client_id"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="Google OAuth Client ID"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="google_client_secret">Client Secret</Label>
              <Input
                id="google_client_secret"
                type="password"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                placeholder={config?.client_secret ? 'Stored (enter to rotate)' : 'Google OAuth Client Secret'}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="google_redirect_uri">Redirect URI</Label>
              <Input
                id="google_redirect_uri"
                value={redirectUri}
                onChange={(e) => setRedirectUri(e.target.value)}
                placeholder="https://your-app.example.com/oauth/callback"
              />
            </div>
            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? <Spinner size="sm" className="mr-2" /> : <ArrowPathIcon className="mr-2 h-4 w-4" />}
                Save Credentials
              </Button>
            </div>
          </CardContent>
        </Card>
      </PageBody>
    </Page>
  );
}
